
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { seoData, organizationSchema, localBusinessSchema } from '../utils/seoData';
import { 
  FaIndustry, 
  FaAward, 
  FaLeaf, 
  FaCogs, 
  FaHandshake, 
  FaBalanceScale, 
  FaRecycle,
  FaRocket,
  FaStar,
  FaShieldAlt,
  FaUsers,
  FaChartLine,
  FaTools,
  FaGem,
  FaFire,
  FaAtom,
  FaPlay,
  FaArrowRight,
  FaTrophy,
  FaLaptopCode,
  FaMedal,
  FaAngleDown
} from 'react-icons/fa';
import './Home.css';
import Navbar from './navbar';

function HomePage() {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [counters, setCounters] = useState({
    tonnage: 0,
    years: 0,
    clients: 0,
    quality: 0
  });

  // Intersection Observer for drone videos (lazy autoplay)
  useEffect(() => {
    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(err => console.log('Video autoplay prevented:', err));
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    // Observe drone videos after component mounts
    setTimeout(() => {
      const droneVideos = document.querySelectorAll('.drone-video');
      droneVideos.forEach(video => videoObserver.observe(video));
    }, 100);

    return () => videoObserver.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Start counter animations when visible
          startCounters();
        }
      },
      { threshold: 0.3 }
    );

    const statsSection = document.querySelector('.bb-stats');
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, []);

  // Counter animation function
  const startCounters = () => {
    const targets = {
      tonnage: 200,
      years: 15,
      clients: 100,
      quality: 100
    };

    const duration = 1500; // 1.5 seconds for faster animation
    const steps = 60; // 60 frames for smooth animation
    const increment = duration / steps;

    Object.keys(targets).forEach(key => {
      let current = 0;
      const target = targets[key];
      const stepValue = target / steps;

      const timer = setInterval(() => {
        current += stepValue;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        
        setCounters(prev => ({
          ...prev,
          [key]: Math.floor(current)
        }));
      }, increment);
    });
  };

  // Mouse tracking for 3D effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Generate subtle background elements
  const generateBackgroundDots = () => {
    return Array.from({ length: 20 }, (_, i) => (
      <div
        key={i}
        className="bg-dot"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 8}s`,
          animationDuration: `${8 + Math.random() * 4}s`
        }}
      />
    ));
  };

  return ( 
    <div className="bb-landing">
      <SEO 
        title={seoData.home.title}
        description={seoData.home.description}
        keywords={seoData.home.keywords}
        canonical={seoData.home.canonical}
        ogImage={seoData.home.ogImage}
        structuredData={[organizationSchema, localBusinessSchema]}
      />
      <Navbar />
      {/* Hero Section */}
      <section className="bb-hero">
        {/* Professional Background Elements */}
        <div className="hero-background">
          <div className="geometric-grid"></div>
          <div className="subtle-orbs">
            <div className="orb orb-1" style={{
              transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`
            }}></div>
            <div className="orb orb-2" style={{
              transform: `translate(${-mousePosition.x * 0.008}px, ${mousePosition.y * 0.012}px)`
            }}></div>
            <div className="orb orb-3" style={{
              transform: `translate(${mousePosition.x * 0.015}px, ${-mousePosition.y * 0.008}px)`
            }}></div>
          </div>
          <div className="circuit-lines">
            {generateBackgroundDots()}
          </div>
        </div>
        
        <div className="bb-hero-overlay">
          <iframe
            className="hero-background-video"
            src="https://www.youtube.com/embed/cmeBzB-XFlM?autoplay=1&mute=1&loop=1&playlist=cmeBzB-XFlM&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1"
            title="Background Video"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          ></iframe>
          <div className="hero-overlay-dark"></div>
          <div className="bb-hero-content" style={{
            transform: `translateY(${scrollY * -0.1}px)`
          }}>
            <div className="bb-logo-large logo-3d">
              <img src="/images/bb-logo.png" alt="BB Industry Logo" style={{
                transform: `rotateY(${mousePosition.x * 0.01}deg) rotateX(${mousePosition.y * 0.01}deg)`
              }} />
            </div>
            <h1 className="typing-text hero-title-3d">Rooted in Craft. Driven by Progress. Committed to Tomorrow.</h1>
            <div className="hero-cta">
              <Link to="/contact/overview" className="primary-btn">
                Connect with Us <FaArrowRight className="btn-icon" />
              </Link>
              <Link to="/about-us/vision-mission" className="secondary-btn">
                <FaPlay className="btn-icon" /> Explore Our Vision
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Mission */}
      <section className="bb-mission">
        <div className="container">
          <div className="mission-content">
            <h2>Your Destination for All Things Aluminium</h2>
            <p className="mission-statement">BB Industry is a destination for many things aluminium. We are makers, traders, and innovators in the aluminium industry, committed to delivering excellence at every step.</p>
            <div className="mission-grid">
              <div className="mission-item floating mission-card-3d" style={{
                transform: `perspective(1000px) rotateY(${mousePosition.x * 0.005}deg) rotateX(${mousePosition.y * 0.005}deg)`
              }}>
                <FaIndustry className="mission-icon" />
                <h3>Manufacturing Excellence</h3>
                <p>State-of-the-art facility in Balangir, Odisha producing high-quality aluminium and steel products with precision and consistency</p>
                <div className="card-glow"></div>
              </div>
              <div className="mission-item floating mission-card-3d" style={{
                transform: `perspective(1000px) rotateY(${mousePosition.x * 0.008}deg) rotateX(${mousePosition.y * 0.008}deg)`
              }}>
                <FaBalanceScale className="mission-icon" />
                <h3>Trading Expertise</h3>
                <p>Comprehensive importing and trading of premium aluminium scraps with unwavering commitment to quality and reliability</p>
                <div className="card-glow"></div>
              </div>
              <div className="mission-item floating mission-card-3d" style={{
                transform: `perspective(1000px) rotateY(${mousePosition.x * 0.006}deg) rotateX(${mousePosition.y * 0.006}deg)`
              }}>
                <FaCogs className="mission-icon" />
                <h3>Advanced Technology</h3>
                <p>Three cutting-edge 4-Hi Rolling Mills delivering superior production capabilities and innovative manufacturing solutions</p>
                <div className="card-glow"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="bb-capabilities">
        <div className="container">
          <h2>Our Core Capabilities</h2>
          <div className="capabilities-grid">
            <div className="capability-card shimmer">
              <div className="capability-icon-wrapper">
                <FaIndustry className="capability-icon" />
              </div>
              <h3>Aluminium Sheets & Blanks</h3>
              <p>Manufacturing high-quality aluminium sheets and blanks with exceptional precision, consistency, and adherence to international standards</p>
            </div>
            <div className="capability-card shimmer">
              <div className="capability-icon-wrapper">
                <FaTools className="capability-icon" />
              </div>
              <h3>Premium Utensils Manufacturing</h3>
              <p>Crafting superior aluminium and stainless steel utensils using state-of-the-art technology and time-honored craftsmanship</p>
            </div>
            <div className="capability-card shimmer">
              <div className="capability-icon-wrapper">
                <FaRecycle className="capability-icon" />
              </div>
              <h3>Aluminium Scrap Trading</h3>
              <p>Comprehensive import and trade operations for all grades of aluminium scraps, backed by expertise and unwavering reliability</p>
            </div>
          </div>
        </div>
      </section>

      {/* Drone Showcase Section */}
      {/* <section className="bb-drone-showcase">
        <div className="container">
          <div className="drone-header">
            <h2>Experience Our Facility</h2>
            <p>Take an aerial tour of our state-of-the-art manufacturing operations</p>
          </div>
          <div className="drone-video-grid">
            <div className="drone-video-container">
              <video controls loop className="drone-video" preload="metadata" loading="lazy">
                <source src="/videos/outdoor.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="video-overlay">
                <h3>Aerial Facility Tour</h3>
                <p>Complete overview of our manufacturing complex</p>
              </div>
            </div>
            <div className="drone-video-container">
              <video controls loop className="drone-video" preload="metadata" loading="lazy">
                <source src="/videos/indoor.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="video-overlay">
                <h3>Production Floor</h3>
                <p>Inside our rolling mills and manufacturing processes</p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Photo Gallery Section */}
      <section className="bb-photo-gallery">
        <div className="container">
          <div className="gallery-header">
            <h2>Our Machinery & Facility</h2>
            <p>Advanced equipment and state-of-the-art manufacturing infrastructure</p>
          </div>
          <div className="photo-gallery-grid">
            <div className="photo-item">
              <img src="/images/gallery/img.jpeg" alt="Rolling Mill Equipment" loading="lazy" />
            </div>
            <div className="photo-item">
              <img src="/images/gallery/img2.jpeg" alt="Manufacturing Machinery" loading="lazy" />
            </div>
            <div className="photo-item">
              <img src="/images/gallery/img3.jpeg" alt="Production Facility" loading="lazy" />
            </div>
            <div className="photo-item">
              <img src="/images/gallery/img5.jpeg" alt="Industrial Equipment" loading="lazy" />
            </div>
            <div className="photo-item">
              <img src="/images/gallery/img6.jpeg" alt="Facility Infrastructure" loading="lazy" />
            </div>
            <div className="photo-item">
              <img src="/images/gallery/img7.jpeg" alt="Advanced Machinery" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Production Excellence */}
      <section className="bb-production">
        <div className="container">
          <div className="production-layout">
            <div className="production-visual">
              <div className="image-gallery">
                <img src="/images/Home/outdoor_unit.jpg" alt="BB Industry Manufacturing Facility Exterior" className="main-facility-image" loading="lazy" />
                <div className="gallery-grid">
                  <img src="/images/Home/DSC021821.jpg" alt="Rolling Mill 1" loading="lazy" />
                  <img src="/images/Home/DSC021819.jpg" alt="Rolling Mill 2" loading="lazy" />
                  <img src="/images/Home/DSC021826.jpg" alt="Quality Control Lab" loading="lazy" />
                  <img src="/images/Home/DSC021834.jpg" alt="Finished Aluminum Products" loading="lazy" />
                </div>
              </div>
            </div>
            <div className="production-content">
              <h2>Production Excellence at Balangir</h2>
              <p>Our state-of-the-art manufacturing facility in Balangir, Odisha represents our commitment to technological advancement and quality production.</p>
              <div className="production-specs">
                <div className="spec-item">
                  <h3>Rolling Capacity</h3>
                  <p>200 tonnes per month with three 4-Hi Rolling Mills</p>
                </div>
                <div className="spec-item">
                  <h3>Aluminium Production</h3>
                  <p>30 tonnes per month of premium aluminium products</p>
                </div>
                <div className="spec-item">
                  <h3>Stainless Steel</h3>
                  <p>10 tonnes per month of high-quality stainless steel</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bb-values">
        <div className="container">
          <h2>Our Values</h2>
          <div className="values-grid">
            <div className="value-item">
              <FaAward className="value-icon" />
              <h3>Quality Excellence</h3>
              <p>Maintaining uncompromising standards and rigorous quality control in every product we manufacture and trade</p>
            </div>
            <div className="value-item">
              <FaHandshake className="value-icon" />
              <h3>Trust & Integrity</h3>
              <p>Building lasting partnerships through transparent communication, ethical practices, and unwavering reliability</p>
            </div>
            <div className="value-item">
              <FaRocket className="value-icon" />
              <h3>Innovation & Progress</h3>
              <p>Continuously advancing our capabilities and technologies to exceed the evolving demands of tomorrow's industry</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="bb-sustainability">
        <div className="container">
          <div className="sustainability-header">
            <h2>Committed to Sustainable Future</h2>
            <p>Leading the aluminum industry with environmentally responsible practices and green technologies</p>
          </div>
          <div className="sustainability-grid">
            <div className="sustainability-item floating">
              <div className="sustainability-icon-wrapper">
                <FaFire className="sustainability-icon" />
              </div>
              <h3>Bio-mass Pellets</h3>
              <p>Utilizing renewable bio-mass pellets as an eco-friendly fuel source for our manufacturing processes, reducing carbon footprint and supporting sustainable energy practices</p>
              <div className="sustainability-stats">
                <span className="stat-number">65%</span>
                <span className="stat-label">Carbon Reduction</span>
              </div>
            </div>
            <div className="sustainability-item floating">
              <div className="sustainability-icon-wrapper">
                <FaAtom className="sustainability-icon" />
              </div>
              <h3>Solar Power Integration</h3>
              <p>Harnessing clean solar energy to power our operations, contributing to renewable energy adoption and minimizing environmental impact across our manufacturing facilities</p>
              <div className="sustainability-stats">
                <span className="stat-number">40%</span>
                <span className="stat-label">Solar Powered</span>
              </div>
            </div>
            <div className="sustainability-item floating">
              <div className="sustainability-icon-wrapper">
                <FaRecycle className="sustainability-icon" />
              </div>
              <h3>Aluminum Recycling Excellence</h3>
              <p>Comprehensive aluminum scrap recycling operations, transforming waste into valuable resources while promoting circular economy principles and material conservation</p>
              <div className="sustainability-stats">
                <span className="stat-number">95%</span>
                <span className="stat-label">Material Recovery</span>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      {/* Industry Stats Section */}
      <section className="bb-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon">
                <FaChartLine />
              </div>
              <div className="stat-content">
                <span className={`stat-number animated-counter ${isVisible ? 'animate' : ''}`}>
                  {counters.tonnage}+
                </span>
                <span className="stat-label">Tonnes Monthly Capacity</span>
                <p className="stat-description">Industry-leading production volume</p>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FaUsers />
              </div>
              <div className="stat-content">
                <span className={`stat-number animated-counter ${isVisible ? 'animate' : ''}`}>
                  {counters.years}+
                </span>
                <span className="stat-label">Years Experience</span>
                <p className="stat-description">Decades of aluminum expertise</p>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FaStar />
              </div>
              <div className="stat-content">
                <span className={`stat-number animated-counter ${isVisible ? 'animate' : ''}`}>
                  {counters.clients}+
                </span>
                <span className="stat-label">Satisfied Clients</span>
                <p className="stat-description">Building trust nationwide</p>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FaShieldAlt />
              </div>
              <div className="stat-content">
                <span className={`stat-number animated-counter ${isVisible ? 'animate' : ''}`}>
                  {counters.quality}%
                </span>
                <span className="stat-label">Quality Assured</span>
                <p className="stat-description">Zero-compromise quality standards</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Why Choose B B Industry Section */}
      <section className="bb-why-choose">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose B B Industry?</h2>
            <p>Leading the aluminum industry with excellence, innovation, and reliability</p>
          </div>
          <div className="why-choose-grid">
            <div className="choice-card">
              <div className="card-number">01</div>
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <FaShieldAlt className="card-icon" />
                </div>
                <h3>Premium Quality Assurance</h3>
                <p>ISO certified manufacturing processes with rigorous quality control at every stage, ensuring products that meet international standards</p>
                <div className="card-highlight"></div>
              </div>
            </div>
            
            <div className="choice-card">
              <div className="card-number">02</div>
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <FaCogs className="card-icon" />
                </div>
                <h3>Advanced Manufacturing</h3>
                <p>State-of-the-art facility with three 4-Hi Rolling Mills and cutting-edge technology for precision manufacturing and exceptional quality control</p>
                <div className="card-highlight"></div>
              </div>
            </div>
            
            <div className="choice-card">
              <div className="card-number">03</div>
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <FaHandshake className="card-icon" />
                </div>
                <h3>Trusted Industry Partner</h3>
                <p>15+ years of experience building lasting relationships with transparent communication and unwavering reliability</p>
                <div className="card-highlight"></div>
              </div>
            </div>
            
            <div className="choice-card">
              <div className="card-number">04</div>
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <FaRocket className="card-icon" />
                </div>
                <h3>Efficient Logistics</h3>
                <p>Streamlined supply chain and logistics network ensuring on-time delivery across India with competitive pricing</p>
                <div className="card-highlight"></div>
              </div>
            </div>
            
            <div className="choice-card">
              <div className="card-number">05</div>
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <FaLeaf className="card-icon" />
                </div>
                <h3>Sustainable Practices</h3>
                <p>Environmentally responsible manufacturing and recycling processes contributing to a sustainable future</p>
                <div className="card-highlight"></div>
              </div>
            </div>
            
            <div className="choice-card">
              <div className="card-number">06</div>
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <FaAward className="card-icon" />
                </div>
                <h3>Industry Excellence</h3>
                <p>Recognized for innovation and quality in aluminum trading and manufacturing with proven track record</p>
                <div className="card-highlight"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bb-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Transform Your Business with B B Industry</h2>
            <p>Discover the perfect alliance of traditional craftsmanship and cutting-edge innovation. Whether you need premium aluminium products, dependable trading partnerships, or comprehensive manufacturing solutions, B B Industry is your catalyst for unprecedented growth and success.</p>
            <div className="cta-buttons">
              <Link to="/contact/enquiry-form" className="primary-btn pulse-glow">
                Start a Conversation <FaArrowRight className="btn-icon" />
              </Link>
              <Link to="/about-us/manufacturing" className="secondary-btn">
                Explore Our Capabilities <FaAngleDown className="btn-icon" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bb-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/images/bb-logo.png" alt="BB Industry Logo" />
              <p>Rooted in Craft. Driven by Progress. Committed to Tomorrow.</p>
            </div>
            <div className="footer-info">
              <h4>BB Industry</h4>
              <p>Manufacturing Facility: Balangir, Odisha</p>
              <p>Email: info@bbindustry.com</p>
              <p>Phone: +91 123 456 7890</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 BB Industry. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;